// The Changes audit family for one booking (Rule Precedence; 0157/0282):
// every Extend, Upgrade and Add Service — what was asked, the therapist's
// answer and reason, the amount, and how it ended.
//
// Admins read every booking and Hub Managers their own hub's (0157's
// policies); a therapist has no read on this table at all (TEND-068).
// C3 (Leon, 2026-10-04): Admin and Superadmin can cancel a pending change.

import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Label } from "@bednbody/ui/components/label";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { toast } from "sonner";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

const OPEN = new Set(["awaiting_therapist", "awaiting_payment"]);

interface ChangeRow {
  id: string;
  kind: string;
  status: string;
  amountPhp: number;
  minutesAdded: number;
  paymentMethod: string;
  declineCode: string | null;
  declineReason: string | null;
  resolution: string | null;
  requestedAt: string;
  serviceName: string | null;
  durationMinutes: number | null;
}

const KIND_LABEL: Record<string, string> = {
  extension: "Extend",
  upgrade: "Upgrade",
  add_service: "Add Service",
};

const STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  awaiting_therapist: { label: "Waiting for therapist", variant: "warning" },
  awaiting_payment: { label: "Waiting for payment", variant: "warning" },
  applied: { label: "Applied", variant: "success" },
  declined: { label: "Declined", variant: "muted" },
  expired: { label: "Expired", variant: "muted" },
  cancelled: { label: "Cancelled", variant: "muted" },
  payment_failed: { label: "Payment failed", variant: "destructive" },
};

const DECLINE_LABEL: Record<string, string> = {
  equipment_unavailable: "Equipment unavailable",
  client_mistake: "Client chose by mistake",
  other: "Other",
};

function useInServiceChanges(bookingId: string, bookingGroupId: string | null) {
  return useQuery({
    queryKey: ["booking-in-service-changes", bookingId, bookingGroupId],
    queryFn: async (): Promise<ChangeRow[]> => {
      // A group's changes live on its rows; one card shows them all.
      let ids = [bookingId];
      if (bookingGroupId) {
        const { data, error } = await supabase
          .from("bookings")
          .select("id")
          .eq("booking_group_id", bookingGroupId);
        if (error) throw error;
        ids = (data ?? []).map((b) => b.id);
      }
      const { data, error } = await supabase
        .from("booking_in_service_changes")
        .select(
          "id, kind, status, amount_php, minutes_added, payment_method, decline_code, decline_reason, resolution, requested_at, to_variant:service_variants!booking_in_service_changes_to_variant_id_fkey(duration_minutes, services(name))",
        )
        .in("booking_id", ids)
        .order("requested_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r) => {
        const to = r.to_variant as {
          duration_minutes: number;
          services: { name: string } | null;
        } | null;
        return {
          id: r.id,
          kind: r.kind,
          status: r.status,
          amountPhp: Number(r.amount_php),
          minutesAdded: r.minutes_added,
          paymentMethod: r.payment_method,
          declineCode: r.decline_code,
          declineReason: r.decline_reason,
          resolution: r.resolution,
          requestedAt: r.requested_at,
          serviceName: to?.services?.name ?? null,
          durationMinutes: to?.duration_minutes ?? null,
        };
      });
    },
  });
}

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const when = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });

export function InServiceChangesCard({
  bookingId,
  bookingGroupId,
  canCancel,
}: {
  bookingId: string;
  bookingGroupId: string | null;
  /** Admin / Superadmin only — the server refuses anyone else either way. */
  canCancel: boolean;
}) {
  const { data } = useInServiceChanges(bookingId, bookingGroupId);
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const cancel = useMutation({
    mutationFn: (vars: { changeId: string; reason: string }) =>
      callEdgeFunction("admin/in-service-changes/cancel", { data: vars }),
    onSuccess: () => {
      toast.success("Change cancelled. The client has been told.");
      setCancelling(null);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["booking-in-service-changes"] });
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't cancel that change."),
  });
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Changes during the service</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {data.map((c) => {
            const status = STATUS[c.status] ?? {
              label: c.status,
              variant: "muted" as BadgeVariant,
            };
            return (
              <li
                key={c.id}
                className="space-y-1 border-b border-border pb-3 last:border-b-0 last:pb-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">
                    {KIND_LABEL[c.kind] ?? c.kind}
                    {c.serviceName ? ` · ${c.serviceName}` : ""}
                    {c.durationMinutes ? ` · ${c.durationMinutes} min` : ""}
                  </p>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {peso(c.amountPhp)} · {c.paymentMethod}
                  {c.minutesAdded > 0 ? ` · +${c.minutesAdded} min` : ""} · {when(c.requestedAt)}
                </p>
                {c.declineCode ? (
                  <p className="text-xs text-muted-foreground">
                    Reason: {DECLINE_LABEL[c.declineCode] ?? c.declineCode}
                    {c.declineReason ? ` — ${c.declineReason}` : ""}
                  </p>
                ) : null}
                {c.status !== "applied" && c.resolution && !c.declineCode ? (
                  <p className="text-xs text-muted-foreground">{c.resolution}</p>
                ) : null}
                {canCancel && OPEN.has(c.status) ? (
                  <Button size="sm" variant="outline" onClick={() => setCancelling(c.id)}>
                    Cancel this change
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </CardContent>

      <Dialog
        open={cancelling !== null}
        onOpenChange={(open) => {
          if (!open) setCancelling(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this change?</DialogTitle>
            <DialogDescription>
              Nothing is added or charged. A paused session resumes with the time it had left, and
              the client is told.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={reasonId}>Reason</Label>
            <Textarea
              id={reasonId}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>
              Back
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length === 0 || cancel.isPending}
              onClick={() =>
                cancelling
                  ? cancel.mutate({ changeId: cancelling, reason: reason.trim() })
                  : undefined
              }
            >
              {cancel.isPending ? "Cancelling…" : "Cancel change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
